import { useState, useEffect } from 'react';
import { usersApi, ApiRequestError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import type { PublicUser, UserRole } from '@apartment-booking/shared';
import { validateEmail, FRIENDS_USER_ID } from '@apartment-booking/shared';

// Helper to generate CSS styles for color patterns
function getPatternStyle(pattern: string, hex: string): React.CSSProperties {
  const base: React.CSSProperties = { backgroundColor: hex };

  switch (pattern) {
    case 'stripes':
      return {
        ...base,
        backgroundImage: `repeating-linear-gradient(90deg, transparent, transparent 3px, rgba(255,255,255,0.4) 3px, rgba(255,255,255,0.4) 6px)`,
      };
    case 'dots':
      return {
        ...base,
        backgroundImage: `radial-gradient(circle at 3px 3px, rgba(255,255,255,0.5) 1.5px, transparent 1.5px)`,
        backgroundSize: '6px 6px',
      };
    case 'diagonal':
      return {
        ...base,
        backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(255,255,255,0.35) 3px, rgba(255,255,255,0.35) 6px)`,
      };
    default:
      return base;
  }
}

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<PublicUser[]>([]);
  const [invitations, setInvitations] = useState<
    Array<{
      id: string;
      email: string;
      role: string;
      expires_at: string;
      created_at: string;
      invited_by: string;
      is_expired: number;
    }>
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<UserRole>('user');
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [isInviting, setIsInviting] = useState(false);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState<PublicUser | null>(null);
  const [editName, setEditName] = useState('');
  const [editRole, setEditRole] = useState<UserRole>('user');
  const [editIsActive, setEditIsActive] = useState(true);
  const [editColor, setEditColor] = useState('');
  const [editPattern, setEditPattern] = useState('solid');
  const [availableColors, setAvailableColors] = useState<Array<{ hex: string; pattern: string; name: string; available: boolean }>>([]);
  const [editError, setEditError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Reset password modal state
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetUser, setResetUser] = useState<PublicUser | null>(null);
  const [resetMode, setResetMode] = useState<'email' | 'temporary'>('email');
  const [resetTempPassword, setResetTempPassword] = useState('');
  const [resetError, setResetError] = useState<string | null>(null);
  const [isResetting, setIsResetting] = useState(false);

  // Disable/Enable confirmation state
  const [showToggleModal, setShowToggleModal] = useState(false);
  const [toggleUser, setToggleUser] = useState<PublicUser | null>(null);
  const [isToggling, setIsToggling] = useState(false);

  // Delete confirmation state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteUser, setDeleteUser] = useState<PublicUser | null>(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchData = async () => {
    try {
      const [usersData, invitationsData] = await Promise.all([
        usersApi.list(),
        usersApi.getInvitations(),
      ]);
      setUsers(usersData.users);
      setInvitations(invitationsData.invitations);
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message);
      } else {
        setError('Failed to load users');
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleInvite = async () => {
    setInviteError(null);

    if (!validateEmail(inviteEmail)) {
      setInviteError('Please enter a valid email address');
      return;
    }

    setIsInviting(true);

    try {
      await usersApi.invite(inviteEmail, inviteRole);
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteRole('user');
      fetchData();
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setInviteError(err.message);
      } else {
        setInviteError('Failed to send invitation');
      }
    } finally {
      setIsInviting(false);
    }
  };

  const handleRevokeInvitation = async (id: string) => {
    try {
      await usersApi.revokeInvitation(id);
      fetchData();
    } catch (err) {
      // Handle error silently or show toast
    }
  };

  const handleResendInvitation = async (id: string) => {
    try {
      const result = await usersApi.resendInvitation(id);
      // Update the invitation in local state to reflect new expiry
      fetchData();
      alert(`Invitation resent to ${result.email}`);
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message);
      } else {
        setError('Failed to resend invitation');
      }
    }
  };

  const openEditModal = async (user: PublicUser) => {
    setEditingUser(user);
    setEditName(user.name);
    setEditRole(user.role);
    setEditIsActive(user.is_active);
    setEditColor(user.color);
    setEditPattern(user.color_pattern || 'solid');
    setEditError(null);
    setShowEditModal(true);

    // Fetch available colors
    try {
      const colorsData = await usersApi.getColors();
      // Mark the user's current color+pattern as available for them
      const userColorPattern = `${user.color}:${user.color_pattern || 'solid'}`;
      const colors = colorsData.colors.map(c => ({
        ...c,
        available: c.available || `${c.hex}:${c.pattern}` === userColorPattern
      }));
      setAvailableColors(colors);
    } catch {
      // Colors are optional, continue without them
    }
  };

  const handleEditUser = async () => {
    if (!editingUser) return;

    setEditError(null);

    if (!editName.trim()) {
      setEditError('Name is required');
      return;
    }

    setIsEditing(true);

    try {
      // Update user info
      await usersApi.update(editingUser.id, {
        name: editName.trim(),
        role: editRole,
        is_active: editIsActive,
      });

      // Update color/pattern if changed
      const colorChanged = editColor !== editingUser.color;
      const patternChanged = editPattern !== (editingUser.color_pattern || 'solid');
      if (colorChanged || patternChanged) {
        await usersApi.updateColor(editingUser.id, editColor, editPattern);
      }

      setShowEditModal(false);
      setEditingUser(null);
      fetchData();
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setEditError(err.message);
      } else {
        setEditError('Failed to update user');
      }
    } finally {
      setIsEditing(false);
    }
  };

  // Reset password handlers
  const openResetModal = (user: PublicUser) => {
    setResetUser(user);
    setResetMode('email');
    setResetTempPassword('');
    setResetError(null);
    setShowResetModal(true);
  };

  const handleResetPassword = async () => {
    if (!resetUser) return;
    setResetError(null);

    if (resetMode === 'temporary' && !resetTempPassword) {
      setResetError('Password is required');
      return;
    }

    setIsResetting(true);
    try {
      await usersApi.resetPassword(
        resetUser.id,
        resetMode,
        resetMode === 'temporary' ? resetTempPassword : undefined
      );
      setShowResetModal(false);
      setResetUser(null);
      alert(
        resetMode === 'email'
          ? `Password reset email sent to ${resetUser.email}`
          : 'Temporary password set. The user has been logged out of all sessions.'
      );
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setResetError(err.message);
      } else {
        setResetError('Failed to reset password');
      }
    } finally {
      setIsResetting(false);
    }
  };

  // Disable/Enable handlers
  const openToggleModal = (user: PublicUser) => {
    setToggleUser(user);
    setShowToggleModal(true);
  };

  const handleToggleActive = async () => {
    if (!toggleUser) return;
    setIsToggling(true);
    try {
      await usersApi.update(toggleUser.id, { is_active: !toggleUser.is_active });
      setShowToggleModal(false);
      setToggleUser(null);
      fetchData();
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message);
      }
      setShowToggleModal(false);
    } finally {
      setIsToggling(false);
    }
  };

  // Delete handlers
  const openDeleteModal = (user: PublicUser) => {
    setDeleteUser(user);
    setDeleteConfirmName('');
    setDeleteError(null);
    setShowDeleteModal(true);
  };

  const handleDeleteUser = async () => {
    if (!deleteUser) return;
    if (deleteConfirmName !== deleteUser.name) {
      setDeleteError('Name does not match');
      return;
    }
    setDeleteError(null);
    setIsDeleting(true);
    try {
      await usersApi.deleteUser(deleteUser.id);
      setShowDeleteModal(false);
      setDeleteUser(null);
      fetchData();
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setDeleteError(err.message);
      } else {
        setDeleteError('Failed to delete user');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  // Helper: can the current user manage (reset/disable/delete) the target user?
  const canManage = (user: PublicUser): boolean => {
    if (user.id === currentUser?.id) return false;
    if (user.id === FRIENDS_USER_ID) return false;
    if (user.role === 'owner' && currentUser?.role !== 'owner') return false;
    return true;
  };

  const canDelete = (user: PublicUser): boolean => {
    if (!canManage(user)) return false;
    if (user.is_primary) return false;
    return true;
  };

  const handleSetPrimary = async (id: string) => {
    if (!confirm('Set this owner as the primary owner? They will receive all booking request notifications.')) {
      return;
    }

    try {
      await usersApi.setPrimary(id);
      fetchData();
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
        <button
          onClick={() => setShowInviteModal(true)}
          className="btn-primary"
        >
          Invite User
        </button>
      </div>

      {/* Users table */}
      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Login
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((user) => (
              <tr key={user.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white font-medium text-sm"
                      style={{ backgroundColor: user.color }}
                    >
                      {user.name[0].toUpperCase()}
                    </div>
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900">
                        {user.name}
                      </div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        user.role === 'owner'
                          ? 'bg-purple-100 text-purple-800'
                          : user.role === 'admin'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {user.role}
                    </span>
                    {user.role === 'owner' && user.is_primary && (
                      <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                        Primary
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      user.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {user.last_login
                    ? new Date(user.last_login).toLocaleDateString()
                    : 'Never'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-2">
                  {/* Edit button - for all users except self */}
                  {user.id !== currentUser?.id && (
                    <button
                      onClick={() => openEditModal(user)}
                      className="text-primary-600 hover:text-primary-900"
                    >
                      Edit
                    </button>
                  )}
                  {/* Reset Password */}
                  {canManage(user) && (
                    <button
                      onClick={() => openResetModal(user)}
                      className="text-primary-600 hover:text-primary-900"
                    >
                      Reset PW
                    </button>
                  )}
                  {/* Disable/Enable toggle */}
                  {canManage(user) && (
                    <button
                      onClick={() => openToggleModal(user)}
                      className={user.is_active ? 'text-amber-600 hover:text-amber-900' : 'text-green-600 hover:text-green-900'}
                    >
                      {user.is_active ? 'Disable' : 'Enable'}
                    </button>
                  )}
                  {/* Set Primary - only for owners who are not primary, only visible to owners */}
                  {currentUser?.role === 'owner' &&
                    user.role === 'owner' &&
                    user.is_active &&
                    !user.is_primary && (
                      <button
                        onClick={() => handleSetPrimary(user.id)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        Set Primary
                      </button>
                    )}
                  {/* Delete */}
                  {canDelete(user) && (
                    <button
                      onClick={() => openDeleteModal(user)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pending invitations */}
      {invitations.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Pending Invitations
          </h2>
          <div className="card overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Expires
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invitations.map((inv) => (
                  <tr key={inv.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {inv.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 capitalize">
                        {inv.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {inv.is_expired ? (
                        <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                          Expired
                        </span>
                      ) : (
                        <span className="text-gray-500">
                          {new Date(inv.expires_at).toLocaleDateString()}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-3">
                      <button
                        onClick={() => handleResendInvitation(inv.id)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        Resend
                      </button>
                      <button
                        onClick={() => handleRevokeInvitation(inv.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Invite modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowInviteModal(false)}
          />
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Invite User
            </h2>

            {inviteError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm mb-4">
                {inviteError}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label htmlFor="invite-email" className="label">
                  Email address
                </label>
                <input
                  id="invite-email"
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="input"
                  placeholder="user@example.com"
                />
              </div>

              <div>
                <label htmlFor="invite-role" className="label">
                  Role
                </label>
                <select
                  id="invite-role"
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as UserRole)}
                  className="input"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  {currentUser?.role === 'owner' && (
                    <option value="owner">Owner</option>
                  )}
                </select>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowInviteModal(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleInvite}
                disabled={isInviting}
                className="btn-primary"
              >
                {isInviting ? 'Sending...' : 'Send Invitation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit user modal */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowEditModal(false)}
          />
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Edit User
            </h2>

            {editError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm mb-4">
                {editError}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="label">Email</label>
                <p className="text-gray-900">{editingUser.email}</p>
              </div>

              <div>
                <label htmlFor="edit-name" className="label">
                  Name
                </label>
                <input
                  id="edit-name"
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="input"
                />
              </div>

              <div>
                <label htmlFor="edit-role" className="label">
                  Role
                </label>
                <select
                  id="edit-role"
                  value={editRole}
                  onChange={(e) => setEditRole(e.target.value as UserRole)}
                  className="input"
                  disabled={editingUser.role === 'owner' && currentUser?.role !== 'owner'}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  {currentUser?.role === 'owner' && (
                    <option value="owner">Owner</option>
                  )}
                </select>
                {editingUser.role === 'owner' && currentUser?.role !== 'owner' && (
                  <p className="text-sm text-gray-500 mt-1">Only owners can change owner roles</p>
                )}
              </div>

              {/* Color picker with patterns */}
              {availableColors.length > 0 && (
                <div>
                  <label className="label">Color & Pattern</label>
                  <div className="grid grid-cols-8 gap-2 mt-1">
                    {availableColors.map((color) => {
                      const isSelected = editColor === color.hex && editPattern === color.pattern;
                      const patternStyle = getPatternStyle(color.pattern, color.hex);
                      return (
                        <button
                          key={`${color.hex}-${color.pattern}`}
                          type="button"
                          onClick={() => {
                            if (color.available) {
                              setEditColor(color.hex);
                              setEditPattern(color.pattern);
                            }
                          }}
                          disabled={!color.available}
                          className={`w-8 h-8 rounded border-2 transition-all ${
                            isSelected
                              ? 'border-gray-900 scale-110 ring-2 ring-gray-400'
                              : 'border-gray-300'
                          } ${!color.available ? 'opacity-30 cursor-not-allowed' : 'hover:scale-105'}`}
                          style={patternStyle}
                          title={`${color.name}${!color.available ? ' (in use)' : ''}`}
                        />
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Patterns: Solid, Stripes, Dots, Diagonal
                  </p>
                </div>
              )}

              <div className="flex items-center gap-2">
                <input
                  id="edit-active"
                  type="checkbox"
                  checked={editIsActive}
                  onChange={(e) => setEditIsActive(e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="edit-active" className="text-sm text-gray-700">
                  Active
                </label>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowEditModal(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleEditUser}
                disabled={isEditing}
                className="btn-primary"
              >
                {isEditing ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset password modal */}
      {showResetModal && resetUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowResetModal(false)}
          />
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Reset Password
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Reset password for <strong>{resetUser.name}</strong> ({resetUser.email})
            </p>

            {resetError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm mb-4">
                {resetError}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="label">Reset Method</label>
                <div className="space-y-2 mt-1">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="reset-mode"
                      value="email"
                      checked={resetMode === 'email'}
                      onChange={() => setResetMode('email')}
                      className="h-4 w-4 text-primary-600"
                    />
                    <span className="text-sm text-gray-700">Send reset email to user</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="reset-mode"
                      value="temporary"
                      checked={resetMode === 'temporary'}
                      onChange={() => setResetMode('temporary')}
                      className="h-4 w-4 text-primary-600"
                    />
                    <span className="text-sm text-gray-700">Set temporary password directly</span>
                  </label>
                </div>
              </div>

              {resetMode === 'temporary' && (
                <div>
                  <label htmlFor="reset-password" className="label">
                    Temporary Password
                  </label>
                  <input
                    id="reset-password"
                    type="text"
                    value={resetTempPassword}
                    onChange={(e) => setResetTempPassword(e.target.value)}
                    className="input"
                    placeholder="Enter temporary password"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Must be 12+ characters with uppercase, lowercase, number, and symbol.
                  </p>
                </div>
              )}

              {resetMode === 'email' && (
                <p className="text-xs text-gray-500">
                  The user will receive an email with a link to set a new password.
                  Their existing sessions will be invalidated.
                </p>
              )}
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowResetModal(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleResetPassword}
                disabled={isResetting}
                className="btn-primary"
              >
                {isResetting ? 'Resetting...' : resetMode === 'email' ? 'Send Reset Email' : 'Set Password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Disable/Enable confirmation modal */}
      {showToggleModal && toggleUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowToggleModal(false)}
          />
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {toggleUser.is_active ? 'Disable Account' : 'Enable Account'}
            </h2>

            {toggleUser.is_active ? (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">
                  Disable the account for <strong>{toggleUser.name}</strong>?
                </p>
                <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-md text-sm">
                  <ul className="list-disc list-inside space-y-1">
                    <li>The user will be logged out of all sessions immediately</li>
                    <li>Pending bookings will be cancelled</li>
                    <li>The user will not be able to log in</li>
                  </ul>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-600">
                Re-enable the account for <strong>{toggleUser.name}</strong>?
                They will be able to log in again.
              </p>
            )}

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowToggleModal(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleToggleActive}
                disabled={isToggling}
                className={toggleUser.is_active
                  ? 'inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50'
                  : 'btn-primary'
                }
              >
                {isToggling
                  ? (toggleUser.is_active ? 'Disabling...' : 'Enabling...')
                  : (toggleUser.is_active ? 'Disable Account' : 'Enable Account')
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteModal && deleteUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowDeleteModal(false)}
          />
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h2 className="text-lg font-semibold text-red-600 mb-4">
              Delete Account
            </h2>

            {deleteError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm mb-4">
                {deleteError}
              </div>
            )}

            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Permanently delete the account for <strong>{deleteUser.name}</strong> ({deleteUser.email})?
              </p>
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md text-sm">
                This action cannot be undone. The user and all associated data will be permanently removed.
              </div>
              <div>
                <label htmlFor="delete-confirm" className="label">
                  Type <strong>{deleteUser.name}</strong> to confirm
                </label>
                <input
                  id="delete-confirm"
                  type="text"
                  value={deleteConfirmName}
                  onChange={(e) => setDeleteConfirmName(e.target.value)}
                  className="input"
                  placeholder={deleteUser.name}
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteUser}
                disabled={isDeleting || deleteConfirmName !== deleteUser.name}
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeleting ? 'Deleting...' : 'Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
